<template>
  <div :class="cols">
    <div class="card">
      <div class="card-header">
        <div id="grid-heading">
          <div class="caption" v-html="caption"></div>
        </div>
      </div>
      <div class="card-body">
        <svg id="grid" width="100%" height="100%">
          <defs>
            <linearGradient id="vertical-bar-gradient">
              <stop offset="0%" stop-color="rgba(192, 192, 192, 0.1)" />
              <stop offset="50%" stop-color="rgba(255, 255, 255, 0.1)" />
            </linearGradient>
            <linearGradient
              id="horizontal-bar-gradient"
              gradientTransform="rotate(90)"
            >
              <stop offset="0%" stop-color="rgba(192, 192, 192, 0.1)" />
              <stop offset="50%" stop-color="rgba(255, 255, 255, 0.1)" />
            </linearGradient>
            <pattern
              id="diagonalHatch"
              width="5"
              height="5"
              patternTransform="rotate(45 0 0)"
              patternUnits="userSpaceOnUse"
            >
              <line
                x1="0"
                y1="0"
                x2="0"
                y2="10"
                style="stroke:#eee; stroke-width:3"
              />
            </pattern>
          </defs>
          <g id="col-labels"></g>
          <g id="row-labels"></g>
          <g id="rows"></g>
        </svg>
      </div>
    </div>
  </div>
</template>

<script>
import constants from "@/constants";
import d3 from "@/assets/d3";
import {
  drawMatrixCell,
  drawHorizontalBarsCell,
  drawVerticalBarsCell,
  drawSingleBarCell
} from "@/drawing-methods";

export default {
  props: {
    cols: { type: String, required: true },
    bus: { type: Object, required: true }
  },
  data: function() {
    return {
      coloringMode: undefined,
      selection: {
        rows: new Set(),
        columns: new Set(),
        nodes: new Set()
      },
      caption: undefined
    };
  },
  created() {
    this.dataService = this.$dataService;
    this.bus.$on(constants.NODE_SELECTED_EVENT, this.nodesChanged);
    this.bus.$on(constants.VTS_SELECTED_EVENT, this.vtsChanged);
    this.bus.$on(constants.COLORING_EVENT, this.handleColoringSelection);
    this.bus.$on(constants.HOVER_EVENT, this.onHover);
    document.addEventListener("keyup", this.keyboardSelection);
  },
  mounted() {
    this.width = this.$el.getBoundingClientRect().width - 20; // respect container padding
    this.height = document
      .getElementById("grid")
      .getBoundingClientRect().height; // respect header(?) padding

    this.griddata = this.dataService.to2DGrid(this.width, this.height);

    // Set exact width/height of the svg
    this.svg = d3
      .select("#grid")
      .attr("width", this.width)
      .attr("height", this.height);

    // Finally paint the grid
    this.initializeGrid();
    this.updateCaption();
  },
  methods: {
    /**
     * Event handler for the `keyup` event.
     * Takes care of arrow key presses and updating the selected row/columns.
     * Has builtin mechanism to keep the selection within the grid (no overflows).
     * @param event
     */
    keyboardSelection: function(event) {
      // There is no prior check on the key that triggered the event.
      const relevantKeys = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];
      if (!relevantKeys.includes(event.key)) {
        return;
      }

      let rowIndices = [];
      this.selection.rows.forEach(r =>
        rowIndices.push(this.dataService.sortedIndex.indexOf(r))
      );

      /* eslint-disable no-case-declarations */
      switch (event.key) {
        case "ArrowUp":
          const minRowIndex = Math.min(...rowIndices);

          if (minRowIndex > 0) {
            const newIndices = [...rowIndices].map(row => row - 1);
            const newSelection = new Set();
            newIndices.forEach(idx =>
              newSelection.add(this.dataService.sortedIndex[idx])
            );
            this.selection.rows = newSelection;
          }
          break;
        case "ArrowDown":
          const maxRowIndex = Math.max(...rowIndices);

          if (maxRowIndex < this.griddata.rows.length - 1) {
            const newIndices = [...rowIndices].map(row => row + 1);
            const newSelection = new Set();
            newIndices.forEach(idx =>
              newSelection.add(this.dataService.sortedIndex[idx])
            );
            this.selection.rows = newSelection;
          }
          break;
        case "ArrowLeft":
          const minCol = Math.min(...this.selection.columns);

          if (minCol > 0) {
            let newSelection = [...this.selection.columns].map(col => col - 1);
            this.selection.columns = new Set(newSelection);
          }
          break;
        case "ArrowRight":
          const maxCol = Math.max(...this.selection.columns);

          if (maxCol < this.griddata.rows[0].cells.length - 1) {
            let newSelection = [...this.selection.columns].map(col => col + 1);
            this.selection.columns = new Set(newSelection);
          }
          break;
      }

      this.handleGridSelection();
    },
    initializeGrid() {
      this.svg
        .select("#col-labels")
        .append("text")
        .attrs({
          x: -32,
          y: 11.5,
          "text-anchor": "middle"
        })
        .text("Timesteps");

      // add column labels once
      let colLabelContainer = this.svg
        .select("#col-labels")
        .attr("transform", "translate(100,0)")
        .selectAll("g")
        .data(this.griddata.colLabels, d => d.id)
        .enter()
        .append("g")
        .attrs({
          transform: d => `translate(${d.x},0)`,
          class: "col-label-container"
        })
        .on("click", this.columnLabelSelected);

      colLabelContainer.append("rect").attrs({
        class: "cell-filler",
        width: d => d.width,
        height: d => d.height,
        x: 0,
        y: 0
      });

      colLabelContainer.append("rect").attrs({
        class: "col-marker",
        width: d => d.width,
        "timestep-id": d => d.id,
        height: 10,
        y: 0
      });

      colLabelContainer
        .append("text")
        .attrs({
          class: "col-label",
          "timestep-id": d => d.id,
          x: d => d.width / 2,
          y: 21.5,
          "text-anchor": "middle"
        })
        .text(d => d.text);

      let rowLabelContainer = this.svg
        .select("#row-labels")
        .attr("transform", "translate(0,30)")
        .selectAll("g")
        .data(this.griddata.rowLabels, d => d.id)
        .enter()
        .append("g")
        .attrs({
          transform: d => `translate(${d.x},${d.y - d.height})`,
          class: "row-label-container"
        })
        .on("click", this.rowLabelSelected);

      rowLabelContainer.append("rect").attrs({
        class: "cell-filler",
        width: 100,
        height: d => d.height,
        y: 0
      });

      rowLabelContainer.append("rect").attrs({
        class: "row-marker",
        width: 10,
        "variant-id": d => d.id,
        height: d => d.height,
        y: 0
      });

      this.svg
        .select("#row-labels")
        .append("text")
        .attrs({
          y: -5
        })
        .text("Variants");

      rowLabelContainer
        .append("text")
        .attrs({
          class: "row-label",
          "variant-id": d => d.id,
          x: 15,
          y: d => d.height / 2 + 5
        })
        .text(d => "v" + d.id);

      let selection = this.svg
        .select("#rows")
        .attr("transform", "translate(100,30)")
        .selectAll("g.row")
        .data(this.griddata.rows, d => d.row_id);

      // Add g.row for every row in the dataset
      let row = selection
        .join("g")
        .attr("id", d => `row-${d.row_id}`)
        .attr("class", "row");

      // Each cell is one group, that contains three elements
      let cols = row.selectAll("g.cell-container").data(
        d => d.cells,
        d => d.cell_id
      );

      cols = cols
        .enter()
        .append("g")
        .attrs({
          height: d => d.height,
          width: d => d.width,
          transform: d => `translate(${d.x},${d.y})`,
          class: "cell-container",
          "row-id": d => d.row_id,
          "column-id": d => d.column_id,
          id: d => d.cell_id
        })
        .on("click", this.cellClicked);

      Object.keys(constants.CELL_TYPE).forEach(type => {
        cols
          .append("g")
          .attrs({ type: constants.CELL_TYPE[type], style: "display: none" });
      });

      cols = cols
        .select(`g[type="${constants.CELL_TYPE.SINGLE_BAR}"]`)
        .attrs({ style: "display: block" });

      // 1. The actual bar that encodes the value
      cols.append("rect").attrs({
        class: "square bg-gradient-secondary",
        width: d => d.width,
        height: d => d.cellData[0].bar_height,
        x: 0,
        y: d => d.cellData[0].filler_height
      });

      // 2. Another rect that is rendered above the bar to fill the whitespace with clickable area
      cols.append("rect").attrs({
        class: "cell-filler",
        x: 0,
        y: 0,
        width: d => d.width,
        height: d => d.cellData[0].filler_height
      });

      // 3. and finally a text node that provides a tooltip
      cols
        .append("title")
        .text(
          d =>
            `Timestep: t${d.column_id}, variant: v${d.row_id}, degree: ${d.cellData[0].value}`
        );
    },
    /**
     * Uses the DataService to convert data to 2D Grid and then creates rows/columns from it.
     */
    updateGrid: function() {
      this.griddata = this.dataService.to2DGrid(
        this.width,
        this.height,
        this.selection
      );
      let colLabelContainer = this.svg
        .select("#col-labels")
        .selectAll("g.col-label-container")
        .data(this.griddata.colLabels, d => d.id)
        .join("g.col-label-container");

      colLabelContainer
        .transition()
        .attr("transform", d => `translate(${d.x},0)`);

      colLabelContainer
        .select("rect.cell-filler")
        .transition()
        .attrs({
          width: d => d.width,
          height: d => d.height
        });

      colLabelContainer
        .select("text.col-label")
        .transition()
        .attrs({
          x: d => d.width / 2
        });

      colLabelContainer.select("text.col-label").classed("selected", d => {
        return this.selection.columns.has(d.id);
      });

      colLabelContainer
        .select("rect.col-marker")
        //.transition()
        .attr("width", d => d.width)
        .classed("selected", d => this.selection.columns.has(d.id));

      let rowLabelContainer = this.svg
        .select("#row-labels")
        .selectAll("g.row-label-container")
        .data(this.griddata.rowLabels, d => d.id)
        .join("g.row-label-container");

      rowLabelContainer
        .transition()
        .attr("transform", d => `translate(${d.x},${d.y - d.height})`);

      rowLabelContainer
        .select("rect.cell-filler")
        .transition()
        .attr("height", d => d.height);

      rowLabelContainer
        .select("rect.row-marker")
        //.transition()
        .attr("height", d => d.height)
        .classed("selected", d => this.selection.rows.has(d.id));

      rowLabelContainer
        .select("text.row-label")
        .transition()
        .attrs({
          y: d => d.height / 2
        });

      rowLabelContainer.select("text.row-label").classed("selected", d => {
        return this.selection.rows.has(d.id);
      });

      rowLabelContainer
        .select("text.row-label-container")
        .classed("selected", d => {
          console.log(d);
          return this.selection.rows.has(d.id);
        });

      const maxValue = this.dataService.getHighestDegree(
        this.selection,
        this.griddata.rows
      );

      for (let row of this.griddata.rows) {
        for (let cellData of row.cells) {
          let cellContainer = this.svg.select(`#${cellData.cell_id}`);
          cellContainer.transition().attrs({
            height: cellData.height,
            width: cellData.width,
            transform: `translate(${cellData.x},${cellData.y})`
          });

          // Hide the other groups
          Object.keys(constants.CELL_TYPE).forEach(type => {
            if (constants.CELL_TYPE[type] !== cellData.type) {
              cellContainer
                .select(`g[type="${constants.CELL_TYPE[type]}"]`)
                .attrs({ style: "display: none" });
            }
          });

          cellContainer = cellContainer
            .select(`g[type="${cellData.type}"]`)
            .attrs({ style: "display: block" });

          switch (cellData.type) {
            case constants.CELL_TYPE.MATRIX:
              drawMatrixCell(cellData, cellContainer);
              break;
            case constants.CELL_TYPE.HORIZONTAL_BARS:
              drawHorizontalBarsCell(
                cellData,
                cellContainer,
                maxValue,
                {
                  over: this.hoverBarOver,
                  out: this.hoverBarOut
                },
                this.dataService
              );
              break;
            case constants.CELL_TYPE.VERTICAL_BARS:
              drawVerticalBarsCell(
                cellData,
                cellContainer,
                maxValue,
                {
                  over: this.hoverBarOver,
                  out: this.hoverBarOut
                },
                this.dataService
              );
              break;
            default:
              drawSingleBarCell(cellData, cellContainer);
          }
        }
      }

      this.highlight();

      this.removeColor();
      this.applyColor();
      this.updateCaption();
    },
    /**
     * Highlight (darken) per node metric bars.
     */
    highlight: function() {
      this.svg.selectAll("rect[node-id]").classed("selected", false);
      if (this.selection.nodes.size > 0) {
        this.svg
          .selectAll(
            Array.from(this.selection.nodes)
              .map(node => `rect[node-id="${node}"]`)
              .join(", ")
          )
          .classed("selected", true);
      }
    },
    /**
     * Event handler for when a cell (read: row and column simultaneously) has been selected.
     * @param e
     */
    cellClicked: function(e) {
      console.log("cell clicked");
      if (
        this.selection.columns.has(e.column_id) &&
        this.selection.rows.has(e.row_id)
      ) {
        this.selection.columns.delete(e.column_id);
        this.selection.rows.delete(e.row_id);
      } else {
        this.selection.columns.add(e.column_id);
        this.selection.rows.add(e.row_id);
      }
      this.handleGridSelection();
    },
    /**
     * Emits the `VTS_SELECTED_EVENT` to `this.bus`.
     */
    handleGridSelection: function() {
      const msg = constants.VTS_SELECTED_EVENT;
      const payload = {
        timesteps: this.selection.columns,
        variants: this.selection.rows,
        source: constants.COMPONENT_NAMES.SVG_GRID
      };
      this.bus.$emit(msg, payload);
      // repaint the grid
      this.updateGrid();
    },
    /**
     * Event handler for when a column label has been clicked.
     */
    columnLabelSelected(e) {
      if (!this.selection.columns.delete(e.id)) {
        this.selection.columns.add(e.id);
      }
      this.handleGridSelection();
    },
    /**
     * Event handler for when a row label has been clicked.
     */
    rowLabelSelected(e) {
      if (!this.selection.rows.delete(e.id)) {
        this.selection.rows.add(e.id);
      }
      this.handleGridSelection();
    },
    /**
     * Resets column/row selection, and triggers a new grid paint.
     * @param e
     */
    nodesChanged(e) {
      this.selection.nodes = e.nodes;
      this.updateGrid();
    },
    vtsChanged(e) {
      if (e.source !== constants.COMPONENT_NAMES.SVG_GRID) {
        this.selection.columns = e.timesteps;
        this.selection.rows = e.variants;
        this.updateGrid();
      }
    },
    onHover(e) {
      this.svg.selectAll("rect[node-id]").classed("hovered", false);
      this.svg.selectAll(`rect[node-id="${e.node}"]`).classed("hovered", true);
    },
    hoverBarOver: function(bar) {
      const msg = constants.HOVER_EVENT;
      const payload = {
        node: bar.id,
        source: constants.COMPONENT_NAMES.SVG_GRID
      };
      this.bus.$emit(msg, payload);
    },
    hoverBarOut: function() {
      const msg = constants.HOVER_EVENT;
      const payload = {
        node: undefined,
        source: constants.COMPONENT_NAMES.SVG_GRID
      };
      this.bus.$emit(msg, payload);
    },
    colorizeNodes: function() {
      [...this.selection.nodes].forEach((nodeId, idx) => {
        this.svg
          .selectAll(`rect[node-id="${nodeId}"]`)
          .attr("style", n => `fill: ${this.$colorService.getColorById(n.id)}`);
      });
    },
    colorizeTimesteps: function() {
      [...this.selection.columns].forEach((col, idx) => {
        this.svg
          .selectAll(`rect.node-bar[column-id="${col}"]`)
          .attr("style", `fill: ${this.$colorService.getColorById(idx)}`);
        this.svg
          .selectAll(`rect.col-marker[timestep-id="${col}"]`)
          .attr("style", `fill: ${this.$colorService.getColorById(idx)}`);
      });
    },
    colorizeVariants: function() {
      [...this.selection.rows].forEach((row, idx) => {
        this.svg
          .selectAll(`rect.node-bar[row-id="${row}"]`)
          .attr("style", `fill: ${this.$colorService.getColorById(idx)}`);
        this.svg
          .selectAll(`rect.row-marker[variant-id="${row}"]`)
          .attr("style", `fill: ${this.$colorService.getColorById(idx)}`);
      });
    },
    applyColor: function() {
      switch (this.coloringMode) {
        case constants.COLORING_TYPE.TIMESTEPS:
          this.colorizeTimesteps();
          break;
        case constants.COLORING_TYPE.VARIANTS:
          this.colorizeVariants();
          break;
        case constants.COLORING_TYPE.NODES:
          this.colorizeNodes();
          break;
      }
    },
    removeColor: function() {
      this.svg.selectAll("rect[node-id]").attr("style", null);
      this.svg.selectAll("rect.col-marker").attr("style", null);
      this.svg.selectAll("rect.row-marker").attr("style", null);
    },
    handleColoringSelection: function(e) {
      this.coloringMode = this.coloringMode === e ? undefined : e;
      this.removeColor();
      this.applyColor();
    },
    updateCaption() {
      let caption = "";
      const nodes = this.selection.nodes;
      const timesteps = this.selection.columns;
      const variants = this.selection.rows;
      const allTimestepsSelected =
        timesteps.size === this.dataService.dataIds.timesteps.length;
      const allVariantsSelected =
        variants.size === this.dataService.dataIds.variants.length;
      const degreeHtml =
        "<a title='The number of incoming and outgoing edges for a node'>degree</a>";
      let nonSelectedSupplement = "";
      if (!allTimestepsSelected && !allVariantsSelected) {
        switch (nodes.size) {
          case 0:
          case this.dataService.dataIds.nodes.length:
            caption += `summed ${degreeHtml} of all nodes`;
            break;
          case 1:
            caption += `${degreeHtml} of the selected node`;
            break;
          default:
            caption += `summed ${degreeHtml} of the selected nodes`;
        }
        nonSelectedSupplement = " in non-selected grid cells;";
      }
      if (timesteps.size > 0) {
        if (!(allTimestepsSelected && allVariantsSelected)) {
          caption += `${nonSelectedSupplement} ${degreeHtml} per node${
            allTimestepsSelected || allVariantsSelected
              ? ""
              : ` within selected timestep${timesteps.size > 1 ? "s" : ""}`
          }`;
        }
        if (variants.size > 0) {
          const matrixPlural = timesteps.size + variants.size > 2;
          if (!allTimestepsSelected && !allVariantsSelected) {
            caption += ` and variant${variants.size > 1 ? "s" : ""}`;
          }
          caption += `${
            caption.length > 0 ? ";" : ""
          } <a title='A matrix-based representation of the respective graph; each marked cell represents an edge connecting the row (start node) to a column (target node)'>adjacency matri${
            matrixPlural ? "ces" : "x"
          }</a>`;
          if (!allTimestepsSelected && allVariantsSelected) {
            caption += ` within selected timestep${
              timesteps.size > 1 ? "s" : ""
            }`;
          } else if (allTimestepsSelected && !allVariantsSelected) {
            caption += ` within selected variant${
              variants.size > 1 ? "s" : ""
            }`;
          } else if (!allTimestepsSelected && !allVariantsSelected) {
            caption += ` at intersection${
              matrixPlural ? "s" : ""
            } of selections`;
          }
        }
      } else if (variants.size > 0) {
        caption += `${nonSelectedSupplement} ${degreeHtml} per node${
          allVariantsSelected
            ? ""
            : ` within selected variant${variants.size > 1 ? "s" : ""}`
        }`;
      }
      this.caption =
        "<a title='A visual grid (matrix/table) with timesteps on the x-axis and different graph variants on the y-axis'><em>Time&ndash;variants grid</em></a>: " +
        caption;
    }
  }
};
</script>

<style lang="scss">
svg#grid {
  & .col-label,
  & .row-label,
  & rect.cell-filler {
    cursor: pointer;
  }

  & text.col-label.selected,
  & text.row-label.selected {
    font-weight: bold;
  }

  & rect.cell-filler {
    fill: #fff;
  }

  & rect.row-marker,
  & rect.col-marker {
    fill: white;
  }

  & rect.row-marker.selected,
  & rect.col-marker.selected {
    fill: silver;
  }

  g.cell-container {
    cursor: pointer;

    rect.square {
      stroke: #fff;
      fill: #eee;
    }

    rect[matrix-cell] {
      &.matrix-empty-cell {
        fill: #fff;
      }
    }
    rect.horizontal-bars-container-background {
      fill: url(#horizontal-bar-gradient);
    }
    rect.vertical-bars-container-background {
      fill: url(#vertical-bar-gradient);
    }
    g.horizontal-bars-container,
    g.vertical-bars-container {
      cursor: pointer;

      rect {
        fill: #ddd;
        &.selected {
          fill: #bbb;
        }
        &.hovered {
          stroke: #111;
        }
      }
    }
  }
}
</style>
